export interface Result<T = unknown> {
  data: T | null;
  error: string | null;
  isSuccess: boolean;
  isFailure: boolean;
}

export const ResultFactory = {
  success: <T>(data: T): Result<T> => ({
    data,
    error: null,
    isSuccess: true,
    isFailure: false
  }),
  failure: <T>(error: string): Result<T> => ({
    data: null,
    error,
    isSuccess: false,
    isFailure: true
  })
};

export const ResultHelpers = {
  handle: async <T>(promise: Promise<T>): Promise<Result<T>> => {
    try {
      const data = await promise;
      return ResultFactory.success(data);
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'Unknown error';
      return ResultFactory.failure(message);
    }
  },
  unwrap: async <T>(
    resultOrPromise: Result<T> | Promise<Result<T>>,
    errorMessage?: string
  ): Promise<T> => {
    const result = await resultOrPromise;
    if (result.isFailure) {
      throw new Error(errorMessage ?? result.error ?? 'Operation failed');
    }
    return result.data as T;
  },
  unwrapWith: async <T, R>(
    resultOrPromise: Result<T> | Promise<Result<T>>,
    mapper: (data: T) => R,
    errorMessage?: string
  ): Promise<R> => {
    const data = await ResultHelpers.unwrap(resultOrPromise, errorMessage);
    return mapper(data);
  }
};
